// UserProfilePage.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import "../styles/UserProfilePage.css";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Navbar from '../components/Navbar';

// Firebase imports
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, onSnapshot } from 'firebase/firestore';


// Your web app's Firebase configuration - directly use the provided config
const firebaseConfig = {
    apiKey: "AIzaSyBR-CzqT3QXkmkDzNeqAtb9LVtfrdJ2j2c",
    authDomain: "mango-bcf17.firebaseapp.com",
    projectId: "mango-bcf17",
    storageBucket: "mango-bcf17.appspot.com",
    messagingSenderId: "504947842650",
    appId: "1:504947842650:web:d4c4e92d33d20d89acc263",
    measurementId: "G-0JTQC98SF7"
};

// Initialize Firebase once outside the component
const firebaseApp = initializeApp(firebaseConfig);
const firestoreDb = getFirestore(firebaseApp);
const firebaseAuth = getAuth(firebaseApp);

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#a98ff3']; // Extended colors

function UserProfilePage() {
    const { userProfile } = useAuth();

    // State for Firestore and user authentication
    // Use the initialized instances directly
    const [db, setDb] = useState(firestoreDb); // Set directly
    const [auth, setAuth] = useState(firebaseAuth); // Set directly
    const [userId, setUserId] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false); // To track when authentication is complete

    // State for user details form
    const [userDetails, setUserDetails] = useState({
        name: '',
        phone: '',
        email: '',
        address: '',
    });
    const [isEditingUserDetails, setIsEditingUserDetails] = useState(false);

    // Set up authentication listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUserId(currentUser.uid);
                setIsAuthReady(true);
                console.log("Firebase Auth: User is signed in:", currentUser.uid);
            } else {
                // Access __initial_auth_token via window to help ESLint, while keeping runtime safety
                const initialAuthToken = (typeof window !== 'undefined' && typeof window.__initial_auth_token !== 'undefined')
                                         ? window.__initial_auth_token
                                         : null;

                if (initialAuthToken) {
                    await signInWithCustomToken(auth, initialAuthToken);
                    console.log("Firebase Auth: Signed in with custom token.");
                } else {
                    // Fallback for local development or if token is not provided
                    const anonymousUser = await signInAnonymously(auth);
                    setUserId(anonymousUser.user.uid);
                    setIsAuthReady(true);
                    console.log("Firebase Auth: Signed in anonymously (fallback):", anonymousUser.user.uid);
                }
            }
        });

        // Cleanup the authentication listener when the component unmounts
        return () => unsubscribe();
    }, [auth]); // Dependency on 'auth' instance

    // Load user details from Firestore
    useEffect(() => {
        // Ensure db, userId, and auth readiness before attempting to fetch data
        if (db && userId && isAuthReady) {
            // Use firebaseConfig.projectId for the 'appId' part of the Firestore path
            const projectIdForPath = firebaseConfig.projectId;
            const userDetailsDocRef = doc(db, `artifacts/${projectIdForPath}/users/${userId}/user_details`, 'profile');

            console.log(`Attempting to fetch user details for userId: ${userId} from path: artifacts/${projectIdForPath}/users/${userId}/user_details/profile`);

            // Set up a real-time listener for the user details document
            const unsubscribe = onSnapshot(userDetailsDocRef, (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setUserDetails(data);
                    console.log("Fetched user details from Firestore:", data);
                } else {
                    console.log("No user details found in Firestore for this user. Initializing empty.");
                    // If no document exists, initialize state with empty values
                    setUserDetails({ name: '', phone: '', email: '', address: '' });
                }
            }, (error) => {
                console.error("Error fetching user details from Firestore:", error);
            });

            // Cleanup the snapshot listener when the component unmounts or dependencies change
            return () => unsubscribe();
        } else if (isAuthReady && !userId) {
            console.warn("Authentication is ready but userId is not available. Cannot fetch user details.");
        }
    }, [db, userId, isAuthReady]); // Re-run when db, userId, or isAuthReady changes

    // Save user details to Firestore whenever userDetails state changes
    useEffect(() => {
        // Only attempt to save if Firestore is initialized, user is authenticated, and there are actual details to save
        if (db && userId && isAuthReady) {
            const projectIdForPath = firebaseConfig.projectId;
            const userDetailsDocRef = doc(db, `artifacts/${projectIdForPath}/users/${userId}/user_details`, 'profile');

            // Check if any detail field has a non-empty value before saving
            const hasDetails = Object.values(userDetails).some(detail => detail !== '');

            if (hasDetails) {
                setDoc(userDetailsDocRef, userDetails, { merge: true }) // Use merge: true to update existing fields without overwriting the entire document
                    .then(() => {
                        console.log("User details successfully written/updated in Firestore!");
                    })
                    .catch((error) => {
                        console.error("Error writing user details to Firestore:", error);
                    });
            } else {
                console.log("User details are empty or not changed, not saving to Firestore.");
                // We won't remove the document if all fields are empty, as the user might just be clearing inputs temporarily.
            }
        }
    }, [userDetails, db, userId, isAuthReady]); // This runs whenever userDetails, db, userId, or isAuthReady changes


    const handleUserDetailsChange = (e) => {
        const { name, value } = e.target;
        setUserDetails(prevDetails => ({
            ...prevDetails,
            [name]: value
        }));
    };

    const handleUserDetailsSubmit = (e) => {
        e.preventDefault();
        setIsEditingUserDetails(false); // Exit edit mode after saving. The useEffect will handle the save to Firestore.
    };

    const calculateTotalIncome = () => {
        return userProfile?.incomeSources?.reduce((sum, source) => sum + (source.amount || 0), 0) || 0;
    };

    const calculateTotalSavings = () => {
        return userProfile?.savingsAccounts?.reduce((sum, account) => sum + (account.balance || 0), 0) || 0;
    };

    const calculateTotalInvestmentValue = () => {
        return userProfile?.investments?.reduce((sum, investment) => sum + (investment.currentValue || 0), 0) || 0;
    };

    const calculateTotalDebt = () => {
        return userProfile?.debts?.reduce((sum, debt) => sum + (debt.balance || 0), 0) || 0;
    };

    const getPersonalityType = () => {
        return userProfile?.financialPersonality || 'Not yet determined';
    };

    const calculateNetWorth = () => {
        const savings = calculateTotalSavings();
        const investments = calculateTotalInvestmentValue();
        const debt = calculateTotalDebt();
        return savings + investments - debt;
    };

    const calculateSavingsRate = () => {
        const totalIncome = calculateTotalIncome();
        const totalSavings = calculateTotalSavings();
        if (totalIncome > 0) {
            return ((totalSavings / totalIncome) * 100).toFixed(2);
        }
        return 'N/A';
    };

    const calculateDebtToIncomeRatio = () => {
        const totalIncome = calculateTotalIncome();
        const totalDebt = calculateTotalDebt();
        if (totalIncome > 0) {
            return ((totalDebt / totalIncome) * 100).toFixed(2);
        }
        return 'N/A';
    };

    // Income Breakdown Data for Pie Chart
    const incomeData = userProfile?.incomeSources?.map(source => ({
        name: source.name,
        value: source.amount,
    })) || [];

    // Savings Goals Data for Bar Chart
    const savingsGoalData = userProfile?.savingsGoals?.map(goal => ({
        name: goal.name,
        saved: goal.currentAmount || 0,
        target: goal.targetAmount || 0,
    })) || [];

    // Investment Portfolio Breakdown Data for Pie Chart
    const investmentData = () => {
        if (userProfile?.investments) {
            const portfolio = {};
            userProfile.investments.forEach(investment => {
                const type = investment.type || 'Other';
                portfolio[type] = (portfolio[type] || 0) + (investment.currentValue || 0);
            });
            return Object.keys(portfolio).map(key => ({ name: key, value: portfolio[key] }));
        }
        return [];
    };

    // Personalized Insights
    const getFinancialInsight = () => {
        const savingsRate = parseFloat(calculateSavingsRate());
        const debtToIncome = parseFloat(calculateDebtToIncomeRatio());
        const netWorth = calculateNetWorth();

        let insights = [];

        if (!isNaN(savingsRate)) {
            if (savingsRate < 10) {
                insights.push("Your savings rate is quite low. Consider creating a budget to identify areas for potential savings.");
            } else if (savingsRate < 20) {
                insights.push("Aim to increase your savings rate to reach your financial goals more quickly.");
            } else {
                insights.push("Excellent savings habits! Keep up the great work.");
            }
        }

        if (!isNaN(debtToIncome)) {
            if (debtToIncome > 50) {
                insights.push("Your debt-to-income ratio is high. Explore strategies for debt reduction.");
            } else if (debtToIncome > 30) {
                insights.push("Consider focusing on paying down your debt to improve your financial flexibility.");
            } else {
                insights.push("Your debt level appears to be well-managed.");
            }
        }

        if (netWorth < 0) {
            insights.push("Your net worth is currently negative. Focus on reducing debt and increasing assets.");
        } else if (netWorth < calculateTotalAnnualExpenses() * 3) { // Basic emergency fund guideline
            insights.push("Consider building an emergency fund to cover at least 3-6 months of living expenses.");
        }

        return insights.length > 0 ? insights.join(" ") : "No specific insights at this time.";
    };

    const calculateTotalAnnualExpenses = () => {
        // This is a placeholder - you'd need to implement expense tracking
        return 0;
    };

    return (
        <div className="user-profile-page-container">
            <Navbar />
            <h2>Your Financial Overview</h2>

            {/* User Details Section */}
            <div className="profile-section user-details-section">
                <h3>Personal Information</h3>
                {isEditingUserDetails ? (
                    <form onSubmit={handleUserDetailsSubmit}>
                        <div className="form-group">
                            <label htmlFor="name">Name:</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={userDetails.name}
                                onChange={handleUserDetailsChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="phone">Phone:</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={userDetails.phone}
                                onChange={handleUserDetailsChange}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="email">Email:</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={userDetails.email}
                                onChange={handleUserDetailsChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="address">Address:</label>
                            <textarea
                                id="address"
                                name="address"
                                value={userDetails.address}
                                onChange={handleUserDetailsChange}
                                rows="3"
                            ></textarea>
                        </div>
                        <button type="submit" className="action-button">Save Details</button>
                        <button type="button" onClick={() => setIsEditingUserDetails(false)} className="action-button cancel-button">Cancel</button>
                    </form>
                ) : (
                    <div>
                        <p><strong>Name:</strong> {userDetails.name || 'Not provided'}</p>
                        <p><strong>Phone:</strong> {userDetails.phone || 'Not provided'}</p>
                        <p><strong>Email:</strong> {userDetails.email || 'Not provided'}</p>
                        <p><strong>Address:</strong> {userDetails.address || 'Not provided'}</p>
                        <button onClick={() => setIsEditingUserDetails(true)} className="action-button">Edit Details</button>
                    </div>
                )}
            </div>

            <div className="profile-section">
                <h3>Financial Personality</h3>
                <p>Your current financial personality type: <strong>{getPersonalityType()}</strong></p>
                <Link to="/financialpersonalityprofiler" className="action-link">Review/Update Your Profile</Link>
            </div>

            <div className="profile-section">
                <h3>Financial Summary</h3>
                <p><strong>Estimated Net Worth: ₹{calculateNetWorth().toFixed(2)}</strong></p>
            </div>

            <div className="profile-section">
                <h3>Income Overview</h3>
                <p>Total Annual Income: <strong>₹{calculateTotalIncome().toFixed(2)}</strong></p>
                <h4>Income Breakdown</h4>
                <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                        <Pie
                            data={incomeData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            label
                        >
                            {incomeData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} labelFormatter={() => "Amount"} />
                    </PieChart>
                </ResponsiveContainer>
                <Link to="/incomeprofile" className="action-link">Manage Income Sources</Link>
            </div>

            <div className="profile-section">
                <h3>Savings Overview</h3>
                <p>Total Savings: <strong>₹{calculateTotalSavings().toFixed(2)}</strong></p>
                <h4>Savings Goal Progress</h4>
                {savingsGoalData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={savingsGoalData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                            <Bar dataKey="saved" fill="#2ecc71" name="Saved" />
                            <Bar dataKey="target" fill="#3498db" name="Target" />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <p>No savings goals recorded yet. <Link to="/savingsprofile" className="action-link">Add a Goal</Link></p>
                )}
                <Link to="/savingsprofile" className="action-link">Manage Savings & Goals</Link>
            </div>

            <div className="profile-section">
                <h3>Investment Overview</h3>
                <p>Total Investment Value: <strong>₹{calculateTotalInvestmentValue().toFixed(2)}</strong></p>
                <h4>Portfolio Breakdown</h4>
                {investmentData().length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                            <Pie
                                data={investmentData()}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                                label
                            >
                                {investmentData().map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} labelFormatter={() => "Value"} />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <p>No investments recorded yet. <Link to="/investmentprofile" className="action-link">Add Investment</Link></p>
                )}
                <Link to="/investmentprofile" className="action-link">Manage Investments</Link>
            </div>

            <div className="profile-section">
                <h3>Debt Overview</h3>
                <p>Total Outstanding Debt: <strong>₹{calculateTotalDebt().toFixed(2)}</strong></p>
                <Link to="/debtprofile" className="action-link">Manage Your Debts</Link>
            </div>

            <div className="profile-section">
                <h3>Key Metrics</h3>
                <p><strong>Savings Rate: {calculateSavingsRate()}%</strong> (Total Savings / Total Income)</p>
                <p><strong>Debt-to-Income Ratio: {calculateDebtToIncomeRatio()}%</strong> (Total Debt / Total Income)</p>
            </div>

            <div className="profile-section">
                <h3>Personalized Insights</h3>
                <p>{getFinancialInsight()}</p>
            </div>

        </div>
    );
}

export default UserProfilePage;
